/**
 * The quote list, one server page at a time, on the S1a contract (backend commit 09f817a).
 *
 *   GET /api/quotes?view&q&sort&dir&page&limit[&date&value&expiring&customerId]
 *     -> { quotations, pagination: { total, page, limit, pages, totalPages } }
 *   GET /api/quotations/stats/filter-counts?q[&date&value&expiring&customerId]
 *     -> { views: { all, draft, ... }, filterCounts }
 *
 * Every request carries `view`, so it always takes the contract path. The server filters,
 * sorts and pages; nothing here filters or sorts again (PILOT-QUOTES-SHELL.md §2.5).
 * What S1a defines, this inherits as is: "recent" is the server's UTC date, and a search
 * follows the chosen sort (no client re-ranking).
 *
 * Pages are cached per URL (cachedFetch). reload() drops every cached /api/quotes and
 * counts page and fetches the current one again: call it after any create, edit, delete or
 * status change (§8.5).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { cachedFetch, invalidateCache } from '../../services/apiCache';

export const PAGE_SIZE = 50;
/** The pipeline shows one board, not pages: as many as the server allows. */
export const BOARD_SIZE = 200;
/** A new search term waits this long before it is sent. */
const SEARCH_DEBOUNCE_MS = 300;

const COUNTS_PATH = '/api/quotations/stats/filter-counts';

/** The filters both requests share. Defaults are left out, as in the URL. */
function filterParams(list) {
  const sp = new URLSearchParams();
  if (list.q) sp.set('q', list.q);
  if (list.date && list.date !== 'all') sp.set('date', list.date);
  if (list.value && list.value !== 'all') sp.set('value', list.value);
  if (list.expiring) sp.set('expiring', '1');
  if (list.customerId) sp.set('customerId', String(list.customerId));
  return sp;
}

/** The list request for a URL's list state. */
export function listRequestUrl(list, { limit = PAGE_SIZE } = {}) {
  const sp = new URLSearchParams({
    view: list.view || 'all',
    sort: list.sort || 'date',
    dir: list.dir || 'desc',
    page: String(list.page || 1),
    limit: String(limit)
  });
  for (const [k, v] of filterParams(list)) sp.set(k, v);
  return `/api/quotes?${sp.toString()}`;
}

/** The counts request for the same filters (the view itself is not one of them). */
export function countsRequestUrl(list) {
  const qs = filterParams(list).toString();
  return qs ? `${COUNTS_PATH}?${qs}` : COUNTS_PATH;
}

const EMPTY = { quotations: [], pagination: { total: 0, page: 1, limit: PAGE_SIZE, pages: 0 } };

/**
 * @param {object} list - useQuoteRoute().list
 * @param {{enabled?: boolean, limit?: number}} [opts]
 */
export default function useQuoteListData(list, { enabled = true, limit = PAGE_SIZE } = {}) {
  const listUrl = useMemo(() => listRequestUrl(list, { limit }), [list, limit]);
  const countsUrl = useMemo(() => countsRequestUrl(list), [list]);

  const [data, setData] = useState(EMPTY);
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [version, setVersion] = useState(0);

  // Only a change of the search term waits; a chip, the sort or a page goes at once.
  const lastQ = useRef(list.q);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    const searchChanged = lastQ.current !== list.q;
    lastQ.current = list.q;

    const run = async () => {
      setLoading(true);
      try {
        const [page, viewCounts] = await Promise.all([
          cachedFetch(listUrl),
          cachedFetch(countsUrl).catch(() => null)
        ]);
        if (cancelled) return;
        setData({
          quotations: Array.isArray(page?.quotations) ? page.quotations : [],
          pagination: page?.pagination || EMPTY.pagination
        });
        if (viewCounts?.views) setCounts(viewCounts.views);
        setError(null);
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    const timer = setTimeout(run, searchChanged ? SEARCH_DEBOUNCE_MS : 0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // list.q is read through listUrl; it's tracked only to tell a search from the rest
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listUrl, countsUrl, enabled, version]);

  const reload = useCallback(() => {
    invalidateCache('/api/quotes');
    invalidateCache(COUNTS_PATH);
    setVersion((v) => v + 1);
  }, []);

  return { ...data, counts, loading, error, reload };
}
